import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { dt } from "../../utils/dt";
import { Machine } from "../../models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import machineLiveStatusApi from "../../api/machineLiveStatusApi";
import {
  hasDocs as hasDocsFn,
  liveDotVisual,
  liveDotState,
  midasDocsSummary,
} from "../../utils/midasReadiness";
import MidasReadinessPopup from "./MidasReadinessPopup";
import { DOC_TYPE_KEYS } from "./readinessPopupParts";

// =====================================================================
//  MidasReadinessProfileCard (DR-3) — compact "MIDAS DATA READINESS" card
//  in the machine-profile LEFT column, between the identity card and the
//  action buttons. Same label/value row style as the identity card
//  (SpecLine). Styles pulled from Paper artboard v2-2 (node KZ-0).
//
//  Renders NOTHING when the machine has no catalog link (manual machines
//  without a catalog model show no readiness this phase — per artboard).
//
//  Live status is fetched via the fail-closed single-machine wrapper
//  (machineLiveStatusApi, SEC-REQ-9). Any error → "not connected". NO
//  "Ask MIDAS" affordance — informational only; "View details ›" opens the
//  shared popup.
// =====================================================================

const c = {
  cardBorder: "#E8EAEE",
  rowBorder: "#F6F7F9",
  label: "#9AA0A8",
  midasDot: "#3498DB",
  liveGreen: "#27AE60",
  oemGold: "#C9962E",
  oemGoldText: "#A87F1E",
  grayDot: "#DADEE3",
  valueStrong: "#3A3F47",
  valueMuted: "#B9BEC6",
  link: "#2E90E5",
  eyebrow: "#8A9098",
};

interface RowProps {
  dotColor: string;
  hollow?: boolean;
  label: string;
  value: string;
  valueColor: string;
  valueWeight?: number;
  isFirst?: boolean;
}

const ReadinessRow: React.FC<RowProps> = ({ dotColor, hollow, label, value, valueColor, valueWeight, isFirst }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
      padding: "8px 0",
      borderTop: isFirst ? "none" : `1px solid ${c.rowBorder}`,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0 }}>
      <Box
        sx={{
          width: "7px",
          height: "7px",
          borderRadius: "50%",
          flexShrink: 0,
          ...(hollow
            ? { backgroundColor: "transparent", border: `1.3px solid ${dotColor}` }
            : { backgroundColor: dotColor }),
        }}
      />
      <Box sx={{ fontSize: "12px", fontFamily: dt.font, lineHeight: "16px", color: c.label, whiteSpace: "nowrap" }}>
        {label}
      </Box>
    </Box>
    <Box
      sx={{
        fontSize: "12px",
        fontWeight: valueWeight ?? 400,
        fontFamily: dt.font,
        lineHeight: "16px",
        color: valueColor,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        textAlign: "right",
      }}
    >
      {value}
    </Box>
  </Box>
);

interface MidasReadinessProfileCardProps {
  machine: Machine;
}

const MidasReadinessProfileCard: React.FC<MidasReadinessProfileCardProps> = ({ machine }) => {
  const { translate } = useTranslation("myMachinery");
  const [popupOpen, setPopupOpen] = useState(false);
  const [connected, setConnected] = useState(false);

  const catalogModel = machine.catalogModel;
  const hasCatalogModel = !!catalogModel;

  // Fail-closed live-status fetch (SEC-REQ-9). Only for the machine instance;
  // any error leaves `connected` false.
  //
  //  - Item 12: skip the fetch entirely when the machine has no catalog model
  //    (the card renders nothing in that case anyway).
  //  - Item 3: reset `connected` to false the instant `machine.id` changes, so
  //    a stale "connected" from the previously-viewed machine can never bleed
  //    across navigation before the new fetch resolves; and ignore an
  //    out-of-order resolution for a machine we've already navigated away from.
  useEffect(() => {
    setConnected(false);
    if (!machine.id || !hasCatalogModel) return;

    let cancelled = false;
    const requestedId = machine.id;
    machineLiveStatusApi
      .getConnected(requestedId)
      .then((r) => {
        if (!cancelled && requestedId === machine.id) setConnected(r.connected);
      })
      .catch(() => {
        if (!cancelled && requestedId === machine.id) setConnected(false);
      });
    return () => {
      cancelled = true;
    };
  }, [machine.id, hasCatalogModel]);

  // Per artboard: no readiness card for machines without a catalog link.
  if (!catalogModel) return null;

  const readiness = catalogModel.readiness;
  const isOem = catalogModel.isOemPartner === true;
  const oemPartnerName = catalogModel.oemPartnerName;
  const manufacturerName = catalogModel.manufacturer?.name || machine.manufacturer?.name || null;

  const hasDocs = hasDocsFn(readiness);
  const liveVisual = liveDotVisual(readiness, { connected });
  const liveState = liveDotState(readiness, { connected });

  // -- Midas docs value --
  // A total-only readiness (totalDocs but no docCounts/docTypes) has docs yet
  // no lit types → fall back to a plain "N docs" count instead of a blank value.
  const docsSummary = midasDocsSummary(readiness);
  const midasDocsValue =
    docsSummary.kind === "types"
      ? docsSummary.types.map((t) => translate(DOC_TYPE_KEYS[t])).join(" · ")
      : docsSummary.kind === "count"
        ? `${docsSummary.count} ${translate(docsSummary.count === 1 ? "summaryDoc" : "summaryDocs")}`
        : translate("noneYet");

  // -- Live data value + colour --
  const liveValue = connected
    ? translate("liveConnected")
    : liveState === "proven" || liveState === "possible"
      ? translate("liveAvailable")
      : translate("liveNotConnected");
  const liveDotColor = liveVisual === "lit" ? c.liveGreen : liveVisual === "hollow" ? c.label : c.grayDot;
  const liveValueColor = connected ? c.liveGreen : c.valueMuted;

  // -- OEM value --
  const oemValue = isOem
    ? (oemPartnerName || translate("oemAvailable"))
    : translate("oemNotOfferedShort", { manufacturer: manufacturerName || translate("theManufacturerNotOfferedBy") });

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: `1px solid ${c.cardBorder}`,
          borderRadius: "10px",
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
          padding: "14px 16px",
          fontFamily: dt.font,
          animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
        }}
      >
        {/* Eyebrow */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: "6px" }}>
          <Box sx={{ fontSize: "11px", fontWeight: 600, fontFamily: dt.font, letterSpacing: "0.08em", lineHeight: "14px", color: c.eyebrow }}>
            {translate("midasDataReadiness")}
          </Box>
        </Box>

        <ReadinessRow
          isFirst
          dotColor={hasDocs ? c.midasDot : c.grayDot}
          label={translate("midasDocs")}
          value={midasDocsValue}
          valueColor={hasDocs ? c.valueStrong : c.valueMuted}
          valueWeight={hasDocs ? 600 : 400}
        />
        <ReadinessRow
          dotColor={liveDotColor}
          hollow={liveVisual === "hollow"}
          label={translate("liveData")}
          value={liveValue}
          valueColor={liveValueColor}
          valueWeight={connected ? 600 : 400}
        />
        <ReadinessRow
          dotColor={isOem ? c.oemGold : c.grayDot}
          label={translate("oemPackage")}
          value={oemValue}
          valueColor={isOem ? c.oemGoldText : c.valueMuted}
          valueWeight={isOem ? 600 : 400}
        />

        {/* View details link */}
        <Box
          onClick={() => setPopupOpen(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: "2px",
            pt: "9px",
            borderTop: `1px solid ${c.rowBorder}`,
            cursor: "pointer",
            "&:hover .rd-view-label": { textDecoration: "underline" },
          }}
        >
          <Box className="rd-view-label" sx={{ fontSize: "12px", fontFamily: dt.font, lineHeight: "16px", color: c.link }}>
            {translate("viewDetails")}
          </Box>
          <Box sx={{ fontSize: "12px", fontFamily: dt.font, lineHeight: "16px", color: c.link }}>›</Box>
        </Box>
      </Box>

      <MidasReadinessPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        readiness={readiness}
        isOemPartner={isOem}
        oemPartnerName={oemPartnerName}
        liveStatus={connected ? "connected" : "disconnected"}
        machineOrModelName={getMachineDisplayName(machine)}
        manufacturerName={manufacturerName}
      />
    </>
  );
};

export default MidasReadinessProfileCard;
